import React, { useState, useEffect, useRef } from 'react';
import 'tailwindcss/tailwind.css';
import { getReels } from '../../../http/Actions/VideoShowAction';
import PlayIcon from "../../../../images/icono-sc-reproducir64.png"
import BackgroundImage from "../../../../images/stupid-comedy-bg2.jpg";

// Componente de Reels para visualizar reels cortos con navegación
export const ReelsSection = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showingEndCard, setShowingEndCard] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Inicialmente silenciado para permitir autoplay
  const [volumeLevel, setVolumeLevel] = useState(1); // Nivel de volumen (0-1)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false); 
  const [progress, setProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const volumeControlRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Cargar los reels al montar el componente
  useEffect(() => {
    setLoading(true);
    getReels(
      (response) => {
        if (response.status === 200 && response.data) {
          setReels(response.data);
        } else {
          setError("Formato de respuesta inesperado");
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message || "Error al cargar los reels");
        setLoading(false);
      }
    );
  }, []);

  // Efecto para cerrar el control de volumen al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target)) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // MODIFICADO: Eliminado el efecto que automáticamente mostraba la tarjeta final
  // cuando se llega al último video, ya que esto estaba causando el problema
  
  // Función para ir al siguiente video
  const goToNext = (e) => {
    e.stopPropagation(); // Evitar que el clic llegue al contenedor y active play/pause
    
    if (showingEndCard) {
      // Si estamos en la tarjeta final, volver al primer video
      setCurrentIndex(0);
      setShowingEndCard(false); // Asegurarnos de desactivar la tarjeta final
      if (hasUserInteracted) {
        setIsPlaying(true);
      }
      return;
    }
    
    if (reels.length > 0) {
      // Si estamos en el último video, mostrar la tarjeta final en lugar de avanzar
      if (currentIndex === reels.length - 1) {
        setShowingEndCard(true);
        setIsPlaying(false);
      } else {
        // Avanzar al siguiente video normalmente
        setCurrentIndex((prevIndex) => prevIndex + 1);
        // Reproducir automáticamente si ya ha habido interacción
        if (hasUserInteracted) {
          setIsPlaying(true);
        }
      }
    }
  };
  
  // Función para ir al video anterior
  const goToPrevious = (e) => {
    e.stopPropagation(); // Evitar que el clic llegue al contenedor y active play/pause
    
    if (showingEndCard) {
      // Si estamos en la tarjeta final, volver al último video
      setShowingEndCard(false);
      if (hasUserInteracted) {
        setIsPlaying(true);
      }
      return;
    }
    
    if (reels.length > 0) {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + reels.length) % reels.length);
      // Reproducir automáticamente si ya ha habido interacción
      if (hasUserInteracted) {
        setIsPlaying(true);
      }
    }
  };
  
  // Función para alternar entre reproducción y pausa
  const togglePlayPause = () => {
    // No hacer nada si estamos mostrando la tarjeta final
    if (showingEndCard) return;
    
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
            // Marcar que el usuario ha interactuado con un video
            if (!hasUserInteracted) {
              setHasUserInteracted(true);
              // Activar el audio automáticamente después de la primera interacción
              videoRef.current.muted = false;
              setIsMuted(false);
            }
          })
          .catch(error => {
          });
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  // Función para alternar el silencio del video
  const toggleMute = (e) => {
    e.stopPropagation(); // Evitar que el clic se propague
    
    if (videoRef.current) {
      // Si está silenciado, desactivar silencio
      if (isMuted) {
        videoRef.current.muted = false;
        setIsMuted(false);
      } else {
        // Si no está silenciado, silenciar
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
    
    // Mostrar/ocultar la barra de volumen
    setShowVolumeSlider(!showVolumeSlider);
  };

  // Función para cambiar el volumen
  const handleVolumeChange = (e) => {
    e.stopPropagation(); // Evitar que el clic se propague
    
    const newVolume = parseFloat(e.target.value);
    setVolumeLevel(newVolume);
    
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      
      // Si el volumen es 0, también silenciar
      if (newVolume === 0) {
        videoRef.current.muted = true;
        setIsMuted(true);
      } else if (isMuted) {
        // Si estaba silenciado y cambia el volumen a >0, quitar silencio
        videoRef.current.muted = false;
        setIsMuted(false);
      }
    }
  };

  // Función para actualizar la barra de progreso
  const updateProgressBar = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration || 0;
      
      if (duration > 0) {
        const progressPercent = (currentTime / duration) * 100;
        setProgress(progressPercent);
      }
    }
  };

  // Función para saltar a un punto específico del video
  const seekTo = (e) => {
    e.stopPropagation(); // Evitar que el clic se propague
    
    const progressBar = e.currentTarget;
    const position = (e.nativeEvent.offsetX / progressBar.offsetWidth);
    
    if (videoRef.current && videoDuration) {
      videoRef.current.currentTime = position * videoDuration;
      updateProgressBar();
    }
  };
  
  // Reinicia la reproducción cuando cambia el video
  useEffect(() => {
    if (videoRef.current && reels.length > 0 && !showingEndCard) {
      videoRef.current.load();
      setProgress(0);
      setVideoDuration(0);
      
      // Aplicar configuraciones de volumen al cargar un nuevo video
      videoRef.current.volume = volumeLevel;
      videoRef.current.muted = isMuted;
      
      // Si no es el primer video y el usuario ya ha interactuado, reproduzca automáticamente
      if (hasUserInteracted) {
        videoRef.current.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(error => {
            setIsPlaying(false);
          });
      } else {
        setIsPlaying(false);
      }
    }

    // Limpiar el intervalo de actualización de progreso al cambiar de video
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [currentIndex, reels.length, hasUserInteracted, showingEndCard]);
  
  // Sincronizar el estado con eventos nativos del video
  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (videoElement && !showingEndCard) {
      // Manejadores de eventos de reproducción
      const handlePlay = () => {
        setIsPlaying(true);
        
        // Iniciar la actualización de la barra de progreso
        if (!progressIntervalRef.current) {
          progressIntervalRef.current = setInterval(updateProgressBar, 100);
        }
      };
      
      const handlePause = () => {
        setIsPlaying(false);
      };
      
      const handleLoadedMetadata = () => {
        setVideoDuration(videoElement.duration);
      };
      
      // Detectar cuando el video termine para mostrar la tarjeta final si es el último
      const handleEnded = () => {
        if (currentIndex === reels.length - 1) {
          setShowingEndCard(true);
        } else {
          goToNext({ stopPropagation: () => {} });
        }
      };
      
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('ended', handleEnded);
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('ended', handleEnded);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        
        // Limpiar el intervalo de actualización del progreso
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      };
    }
  }, [videoRef.current, currentIndex, reels.length, showingEndCard]);

  // Verificar si es el primer o el último video/endcard
  const isFirstVideo = currentIndex === 0 && !showingEndCard;
  const isLastItem = showingEndCard; // La tarjeta final es considerada como el último elemento

  // Obtener el icono de volumen apropiado según el estado
  const getVolumeIcon = () => {
    if (isMuted) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    } else if (volumeLevel <= 0.3) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217z" clipRule="evenodd" />
        </svg>
      );
    } else if (volumeLevel <= 0.7) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM11.297 9.703a1 1 0 00-1.414 1.414 2 2 0 010 2.83 1 1 0 001.414 1.414 4 4 0 000-5.657z" clipRule="evenodd" />
        </svg>
      );
    } else {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
        </svg>
      );
    }
  };

  // Renderiza un mensaje de carga o error si es necesario
  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-8 md:mb-12">
            Cargando reels...
          </h2>
          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto"></div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 md:py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
            Error al cargar los reels
          </h2>
          <p className="text-red-500">{error}</p>
        </div>
      </section>
    );
  }

  // Si no hay reels disponibles
  if (reels.length === 0) {
    return (
      <section className="py-12 md:py-16 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
            No hay reels disponibles
          </h2>
        </div>
      </section>
    );
  }

  // Si tenemos reels, renderiza el componente principal
  return (
    <section 
      className="py-12 md:py-16 text-white relative"
      style={{
        backgroundImage: `url(${BackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay oscuro para mejorar la legibilidad del texto */}
      <div className="absolute inset-0 bg-black bg-opacity-70"></div>
      
      {/* El resto del contenido debe tener position relative para aparecer sobre el overlay */}
      <div className="relative z-10">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-8 md:mb-12 text-center">
            Prueba de contenido
          </h2>
          
          <div className="relative max-w-md mx-auto h-[60vh] md:h-[80vh] max-h-[800px]">
            {/* Contenedor principal del reel */}
            <div 
              className="relative h-full bg-black rounded-xl overflow-hidden"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              onTouchStart={() => setIsHovering(true)}
              onTouchEnd={() => {
                // Mantener los controles visibles por un momento después de tocar
                setTimeout(() => setIsHovering(false), 3000);
              }}
            >
              {showingEndCard ? (
                // Tarjeta final después de ver todos los videos
                <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-red-900 flex flex-col items-center justify-center p-6 text-center">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    ¡Has visto todos nuestros videos!
                  </h3>
                  <p className="text-lg md:text-xl text-white mb-8">
                    Si quieres disfrutar de más contenido exclusivo,<br />¡suscríbete a nuestro servicio!
                  </p>
                  <button 
                    className="bg-red-600 hover:bg-red-700 text-white py-3 px-8 rounded-full font-bold text-lg shadow-lg transform hover:scale-105 transition duration-300"
                    onClick={() => {
                      // Aquí podrías implementar la lógica para suscribirse
                      alert("¡Gracias por querer suscribirte!");
                    }}
                  >
                    Suscribirme ahora
                  </button>
                </div>
              ) : (
                <>
                  {/* Overlay para el título y autor - En la parte superior */}
                  <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-3 md:p-4 z-10 transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0'}`}>
                    <h3 className="text-lg md:text-xl font-bold text-white">{reels[currentIndex].titulo}</h3>
                    {reels[currentIndex].comediantes && reels[currentIndex].comediantes.length > 0 && (
                      <p className="text-sm md:text-base text-gray-300">
                        {reels[currentIndex].comediantes
                          .filter(c => c.esPrincipal)
                          .map(c => c.nombre)
                          .join(", ") || reels[currentIndex].comediantes[0].nombre}
                      </p>
                    )}
                  </div>
                  
                  {/* Contenedor del video con click para reproducir/pausar */}
                  <div 
                    ref={videoContainerRef}
                    className="absolute inset-0 cursor-pointer"
                    onClick={togglePlayPause}
                  >
                    {/* Video actual */}
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      playsInline
                      loop
                      muted={isMuted}
                      controls={false} // Quitamos los controles nativos para usar nuestro propio control
                    >
                      <source src={reels[currentIndex].videoUrl} type="video/mp4" />
                      Tu navegador no soporta reels.
                    </video>
                    
                    {/* Indicador de Play/Pause visible cuando el video está pausado */}
                    {!isPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        {/* Contenedor reducido con un borde delgado */}
                        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'rgba(0, 0, 0, 0.3)', border: '2px solid rgba(255, 255, 255, 0.7)' }}>
                          {/* Icono de reproducción con tamaño ajustado */}
                          <img 
                            src={PlayIcon}
                            alt="Reproducir"
                            className="w-12 h-12 object-contain"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Mensaje de instrucción para el primer video si no ha habido interacción */}
                    {currentIndex === 0 && !hasUserInteracted && !isPlaying && (
                      <div className="absolute bottom-8 left-0 right-0 text-center text-white bg-black/40 py-2 px-4 mx-auto max-w-xs rounded-full">
                        Toca para reproducir
                      </div>
                    )}
                  </div>

                  {/* Barra de progreso - Visible al pasar el cursor o cuando está pausado */}
                  <div 
                    className={`absolute bottom-0 left-0 right-0 h-1 bg-gray-600/50 cursor-pointer transition-opacity duration-300 z-20 ${(isHovering || !isPlaying) ? 'opacity-100' : 'opacity-0'}`}
                    onClick={seekTo}
                  >
                    <div 
                      className="h-full bg-red-600" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>

                  {/* Control de volumen - Visible al pasar el cursor o cuando está pausado */}
                  <div 
                    ref={volumeControlRef}
                    className={`absolute bottom-4 right-4 flex items-center transition-opacity duration-300 z-20 ${(isHovering || !isPlaying) ? 'opacity-100' : 'opacity-0'}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Barra de volumen - Siempre visible */}
                    <div className="mr-2 bg-black/50 px-2 py-1 rounded-full backdrop-blur-sm">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volumeLevel}
                        onChange={handleVolumeChange}
                        className="w-20 h-1 bg-gray-400 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    
                    {/* Botón de silencio */}
                    <button 
                      onClick={toggleMute}
                      className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-full backdrop-blur-sm transition-all"
                      aria-label={isMuted ? "Activar sonido" : "Silenciar"}
                    >
                      {getVolumeIcon()}
                    </button>
                  </div>
                </>
              )}
              
              {/* Botón de navegación anterior - Solo visible cuando NO estamos en el primer video */}
              {!isFirstVideo && (
                <div className={`absolute inset-y-0 left-0 flex items-center transition-opacity duration-300 ${isHovering || showingEndCard ? 'opacity-100' : 'opacity-0'} z-20`}>
                  <button 
                    onClick={goToPrevious}
                    className="bg-black/30 hover:bg-black/50 text-white p-1 md:p-2 rounded-full ml-2 md:ml-3 backdrop-blur-sm transition-all"
                    aria-label="Video anterior"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Botón de navegación siguiente - Solo visible cuando NO estamos en la tarjeta final */}
              {!isLastItem && (
                <div className={`absolute inset-y-0 right-0 flex items-center transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0'} z-20`}>
                  <button 
                    onClick={goToNext}
                    className="bg-black/30 hover:bg-black/50 text-white p-1 md:p-2 rounded-full mr-2 md:mr-3 backdrop-blur-sm transition-all"
                    aria-label="Siguiente video"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            
            {/* Indicador de posición */}
            <div className="flex justify-center mt-4">
              {[...reels, { isEndCard: true }].map((_, index) => (
                <div 
                  key={index} 
                  className={`h-2 w-2 mx-1 rounded-full ${
                    (index === reels.length && showingEndCard) || 
                    (index === currentIndex && !showingEndCard) 
                      ? 'bg-red-600' 
                      : 'bg-gray-500'
                  }`}
                  onClick={() => {
                    if (index === reels.length) {
                      // Clic en el indicador de la tarjeta final
                      setShowingEndCard(true);
                    } else {
                      // Clic en un indicador de video
                      setCurrentIndex(index);
                      setShowingEndCard(false);
                      // Si el usuario ya ha interactuado, reproducir automáticamente
                      if (hasUserInteracted) {
                        setIsPlaying(true);
                      }
                    }
                  }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};